import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, TextField, Button,
  InputAdornment, CircularProgress, Divider, IconButton,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack,
} from '@mui/material';
import {
  ArrowBack  as ArrowBackIcon,
  Phone      as PhoneIcon,
  Email      as EmailIcon,
  Add        as AddIcon,
  Edit       as EditIcon,
  SaveAlt    as SaveIcon,
  Schedule   as ScheduleIcon,
  Cancel     as CancelIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import OutreachPlaceAutocomplete from '../components/OutreachPlaceAutocomplete';
import NoteCard from '../components/notes/NoteCard';
import { parseNotesBlob } from '../utils/noteUtils';

// ---- helpers -------------------------------------------------------

const EMPTY_FORM = {
  business_name: '',
  contact_name:  '',
  contact_email: '',
  contact_phone: '',
  website:       '',
  location:      '',
  suburb:        '',
  state:         '',
};

function contactToForm(c) {
  return {
    business_name: c.business_name  || '',
    contact_name:  c.contact_name   || '',
    contact_email: c.contact_email  || '',
    contact_phone: c.contact_phone  || '',
    website:       c.website        || '',
    location:      c.location       || '',
    suburb:        c.suburb         || '',
    state:         c.state          || '',
  };
}

const STATUS_MAP = {
  save:           'contacted',
  follow_up:      'follow_up',
  not_interested: 'not_interested',
  create_offer:   'offer_created', // converted only when an order is actually placed
};

function LabeledValue({ label, value }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </Box>
  );
}

// ====================================================================
export default function OutreachContactForm() {
  const { id }       = useParams();
  const locState     = useLocation();
  const navigate     = useNavigate();
  const { getToken } = useAuth();

  const isEdit = !!id;

  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...(locState.state?.prefill || {}),
  }));

  const [loading,  setLoading]  = useState(isEdit && !locState.state?.contact);
  const [saving,   setSaving]   = useState(null);

  // ---- New-contact phase state ------------------------------------
  const [savedContact,     setSavedContact]     = useState(null);
  const [isEditingDetails, setIsEditingDetails] = useState(false);

  // ---- Notes list (used in Phase 2 and edit mode) -----------------
  const [notes,      setNotes]      = useState([]);
  const [newNote,    setNewNote]    = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // ---- Follow-up dialog -------------------------------------------
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpErr,  setFollowUpErr]  = useState(false);

  // ---- Not-interested dialog (both modes) ------------------------
  const [notIntOpen,   setNotIntOpen]   = useState(false);
  const [notIntReason, setNotIntReason] = useState('');
  const [notIntErr,    setNotIntErr]    = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ---- Auth helpers -----------------------------------------------
  const authHeaders = useCallback(async () => {
    const token = await getToken();
    const { fetchCSRFToken } = await import('../utils/csrfProtection');
    const csrf = await fetchCSRFToken();
    return {
      'Authorization':  `Bearer ${token}`,
      'Content-Type':   'application/json',
      'X-CSRF-Token':   csrf,
    };
  }, [getToken]);

  const readHeaders = useCallback(async () => {
    const token = await getToken();
    return { 'Authorization': `Bearer ${token}` };
  }, [getToken]);

  // ---- Load existing contact (edit mode) --------------------------
  useEffect(() => {
    if (!isEdit) return;
    const existing = locState.state?.contact;
    if (existing) {
      setForm(contactToForm(existing));
      setFollowUpDate(existing.follow_up_date ? existing.follow_up_date.slice(0, 10) : '');
      setNotes(parseNotesBlob(existing.notes || '', { idPrefix: `outreach_${id}`, noteType: 'general' }));
    } else {
      (async () => {
        setLoading(true);
        try {
          const headers = await readHeaders();
          const res  = await fetch(`${API_URL}/outreach/contacts/${id}`, { headers });
          const data = await res.json();
          if (data.success) {
            setForm(contactToForm(data.data));
            setFollowUpDate(data.data.follow_up_date ? data.data.follow_up_date.slice(0, 10) : '');
            setNotes(parseNotesBlob(data.data.notes || '', { idPrefix: `outreach_${id}`, noteType: 'general' }));
          } else {
            showSnackbar('Failed to load contact', 'error');
          }
        } catch {
          showSnackbar('Failed to load contact', 'error');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id]); // eslint-disable-line

  function showSnackbar(message, severity = 'success') {
    setSnackbar({ open: true, message, severity });
  }

  function field(key) {
    return {
      value:    form[key],
      onChange: (e) => setForm(f => ({ ...f, [key]: e.target.value })),
    };
  }

  // ---- Core persist helper ----------------------------------------
  async function persist(contactId, payload) {
    const headers = await authHeaders();
    const url = contactId
      ? `${API_URL}/outreach/contacts/${contactId}`
      : `${API_URL}/outreach/contacts`;
    const res  = await fetch(url, {
      method: contactId ? 'PUT' : 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data.data;
  }

  // ---- Add a note to a contact -----------------------------------
  async function postNote(contactId, content) {
    const headers = await authHeaders();
    const res = await fetch(`${API_URL}/outreach/contacts/${contactId}/notes`, {
      method:  'POST',
      headers,
      body:    JSON.stringify({ content }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data.data;
  }

  // ---- Notes list: add note button --------------------------------
  async function handleAddNote(targetId) {
    const cid = targetId || savedContact?.id || id;
    if (!newNote.trim() || !cid) return;
    setSavingNote(true);
    try {
      const note = await postNote(cid, newNote.trim());
      setNotes(prev => [note, ...prev]);
      setNewNote('');
    } catch (err) {
      showSnackbar(err.message || 'Failed to save note', 'error');
    } finally {
      setSavingNote(false);
    }
  }

  function navigateToOffer(contact) {
    navigate('/app/sales-workflow', {
      state: {
        openCreateOffer: true,
        outreach_contact_id: contact.id,       // so SalesWorkflow can link the offer back
        prefillOrg: contact.organization_id
          ? { id: contact.organization_id, name: contact.business_name }
          : { id: null, name: contact.business_name, primaryEmail: contact.contact_email, primaryPhone: contact.contact_phone },
      },
    });
  }

  // ==== PHASE 1: "Add" button ======================================
  async function handleAdd() {
    if (!form.business_name.trim()) { showSnackbar('Business name is required', 'error'); return; }
    setSaving('add');
    try {
      const contact = await persist(null, { ...form, status: 'contacted' });
      setSavedContact(contact);
    } catch (err) {
      showSnackbar(err.message || 'Failed to add contact', 'error');
    } finally {
      setSaving(null);
    }
  }

  // ==== PHASE 1b: "Update Details" =================================
  async function handleUpdateDetails() {
    if (!form.business_name.trim()) { showSnackbar('Business name is required', 'error'); return; }
    setSaving('update');
    try {
      const contact = await persist(savedContact.id, {
        ...form,
        status: savedContact.status,
      });
      setSavedContact(contact);
      setIsEditingDetails(false);
    } catch (err) {
      showSnackbar(err.message || 'Update failed', 'error');
    } finally {
      setSaving(null);
    }
  }

  // ==== PHASE 2: outcome ===========================================
  async function handleOutcome(actionKey, extra = {}) {
    setSaving(actionKey);
    try {
      const contact = await persist(savedContact.id, {
        ...contactToForm(savedContact),
        status: STATUS_MAP[actionKey],
        ...extra,
      });
      if (actionKey === 'create_offer') {
        navigateToOffer(contact);
      } else {
        navigate('/app/outreach');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to save', 'error');
      setSaving(null);
    }
  }

  // ==== EDIT MODE: outcome =========================================
  async function handleEditOutcome(actionKey, extra = {}) {
    setSaving(actionKey);
    try {
      const contact = await persist(id, {
        ...form,
        status: STATUS_MAP[actionKey],
        ...extra,
      });
      if (actionKey === 'create_offer') {
        navigateToOffer(contact);
      } else {
        navigate('/app/outreach');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to save', 'error');
      setSaving(null);
    }
  }

  // ---- Follow-up dialog -------------------------------------------
  function openFollowUp() { setFollowUpErr(false); setFollowUpOpen(true); }

  function confirmFollowUp() {
    if (!followUpDate) { setFollowUpErr(true); return; }
    setFollowUpOpen(false);
    if (isEdit) {
      handleEditOutcome('follow_up', { follow_up_date: followUpDate });
    } else {
      handleOutcome('follow_up', { follow_up_date: followUpDate });
    }
  }

  // ---- Not-interested dialog (both modes) ------------------------
  function openNotInt() { setNotIntReason(''); setNotIntErr(false); setNotIntOpen(true); }

  async function confirmNotInt() {
    if (!notIntReason.trim()) { setNotIntErr(true); return; }
    setNotIntOpen(false);
    setSaving('not_interested');

    const contactId = isEdit ? id : savedContact?.id;
    try {
      // 1. Save the reason as a note
      const note = await postNote(contactId, notIntReason.trim());
      setNotes(prev => [note, ...prev]);

      // 2. Update status
      const contact = await persist(contactId, {
        ...(isEdit ? form : contactToForm(savedContact)),
        status: 'not_interested',
      });
      if (isEdit) {
        navigate('/app/outreach');
      } else {
        void contact; // already navigating
        navigate('/app/outreach');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to save', 'error');
      setSaving(null);
    }
  }

  // ---- Loading screen ---------------------------------------------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ---- Derived booleans -------------------------------------------
  const showForm    = isEdit || !savedContact || isEditingDetails;
  const showPhase2  = !isEdit && !!savedContact && !isEditingDetails;
  // The contact ID for note operations
  const activeId    = isEdit ? id : savedContact?.id;

  // ==================================================================
  // Notes panel — shared between Phase 2 and edit mode
  // ==================================================================
  const NotesPanel = (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Notes</Typography>

      {/* Add note row */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={2}
          placeholder="Add a note… (Ctrl+Enter to save)"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleAddNote(activeId); }}
        />
        <Button
          variant="contained"
          sx={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
          startIcon={savingNote ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          disabled={!newNote.trim() || savingNote}
          onClick={() => handleAddNote(activeId)}
        >
          Add
        </Button>
      </Box>

      {/* Notes history */}
      {notes.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary">
            No notes yet.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeIcon sx={{ fontSize: 14 }} />
            Notes history ({notes.length})
          </Typography>
          {notes.map((note, i) => (
            <NoteCard key={note.id || i} note={note} />
          ))}
        </Stack>
      )}
    </Paper>
  );

  // ==================================================================
  // Render
  // ==================================================================
  return (
    <Container maxWidth="md" sx={{ py: 3 }}>

      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/app/outreach')} title="Back to Outreach">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {isEdit
              ? 'Edit Contact'
              : savedContact
              ? isEditingDetails ? 'Edit Details' : 'Contact Added'
              : 'New Contact'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEdit
              ? 'Update contact details and choose an outcome below.'
              : savedContact
              ? isEditingDetails
                ? 'Make your changes, then click Update Details.'
                : 'Choose what happens next, or edit the contact details below.'
              : 'Fill in the contact details, then click Add.'}
          </Typography>
        </Box>
      </Box>

      {/* ================================================================
          FORM — Phase 1, Phase 1b, and edit mode
      ================================================================ */}
      {showForm && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2.5 }}>
            Contact Details
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Business Name *"
              fullWidth
              autoFocus={!isEdit && !isEditingDetails}
              {...field('business_name')}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Contact Name" fullWidth {...field('contact_name')} />
              <TextField label="Website"      fullWidth {...field('website')} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Phone" fullWidth {...field('contact_phone')}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
              />
              <TextField
                label="Email" fullWidth {...field('contact_email')}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
              />
            </Box>

            <Divider />

            {/* Google Maps autocomplete — populates suburb/state/location in state,
                shown visually; the text fields below are hidden.                   */}
            <OutreachPlaceAutocomplete
              onPlaceSelected={({ suburb, state, location }) =>
                setForm(f => ({ ...f, suburb, state, location }))
              }
              initialLocation={form.location}
            />

            {/* Suburb and State kept in state (filled by autocomplete) but not
                shown as form fields — saving vertical space and avoiding redundancy */}
          </Box>

          {/* Phase 1b action buttons */}
          {isEditingDetails && (
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setForm(contactToForm(savedContact));
                  setIsEditingDetails(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!!saving || !form.business_name.trim()}
                onClick={handleUpdateDetails}
              >
                {saving === 'update'
                  ? <CircularProgress size={20} color="inherit" />
                  : 'Update Details'}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* ================================================================
          Phase 1 — "Add" button
      ================================================================ */}
      {!isEdit && !savedContact && (
        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={saving === 'add' ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
          disabled={!form.business_name.trim() || !!saving}
          onClick={handleAdd}
          sx={{ py: 1.5, mb: 2 }}
        >
          {saving === 'add' ? 'Adding…' : 'Add'}
        </Button>
      )}

      {/* ================================================================
          Edit mode — Notes + Outcomes
      ================================================================ */}
      {isEdit && (
        <>
          {NotesPanel}

          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Outcome</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Save the updated contact with a new status.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              {[
                { key: 'save',           label: 'Save Changes',    color: 'primary', icon: <SaveIcon /> },
                { key: 'follow_up',      label: 'Follow Up Later', color: 'warning', icon: <ScheduleIcon /> },
                { key: 'not_interested', label: 'Not Interested',  color: 'error',   icon: <CancelIcon /> },
                { key: 'create_offer',   label: 'Create Offer',    color: 'success', icon: <TrendingUpIcon /> },
              ].map(({ key, label, color, icon }) => (
                <Button
                  key={key}
                  variant="outlined"
                  color={color}
                  size="large"
                  startIcon={saving === key ? <CircularProgress size={18} color="inherit" /> : icon}
                  disabled={!!saving}
                  onClick={() => {
                    if (key === 'follow_up')       openFollowUp();
                    else if (key === 'not_interested') openNotInt();
                    else handleEditOutcome(key);
                  }}
                  sx={{ py: 1.5 }}
                >
                  {saving === key ? 'Saving…' : label}
                </Button>
              ))}
            </Box>
          </Paper>
        </>
      )}

      {/* ================================================================
          Phase 2 — Notes + Outcome + Read-only card
      ================================================================ */}
      {showPhase2 && (
        <>
          {NotesPanel}

          {/* Outcomes */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Outcome</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose what happens next with this contact.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              <Button
                variant="outlined" color="warning" size="large"
                startIcon={saving === 'follow_up' ? <CircularProgress size={18} color="inherit" /> : <ScheduleIcon />}
                disabled={!!saving}
                onClick={openFollowUp}
                sx={{ py: 1.5 }}
              >
                {saving === 'follow_up' ? 'Saving…' : 'Follow Up Later'}
              </Button>
              <Button
                variant="outlined" color="error" size="large"
                startIcon={saving === 'not_interested' ? <CircularProgress size={18} color="inherit" /> : <CancelIcon />}
                disabled={!!saving}
                onClick={openNotInt}
                sx={{ py: 1.5 }}
              >
                {saving === 'not_interested' ? 'Saving…' : 'Not Interested'}
              </Button>
              <Button
                variant="outlined" color="success" size="large"
                startIcon={saving === 'create_offer' ? <CircularProgress size={18} color="inherit" /> : <TrendingUpIcon />}
                disabled={!!saving}
                onClick={() => handleOutcome('create_offer')}
                sx={{ py: 1.5 }}
              >
                {saving === 'create_offer' ? 'Saving…' : 'Create Offer'}
              </Button>
            </Box>
          </Paper>

          {/* Read-only contact details */}
          <Paper elevation={1} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Contact Details</Typography>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => {
                  setForm(contactToForm(savedContact));
                  setIsEditingDetails(true);
                }}
              >
                Edit Details
              </Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <LabeledValue label="Business Name"  value={savedContact.business_name} />
              <LabeledValue label="Contact Name"   value={savedContact.contact_name} />
              <LabeledValue label="Phone"          value={savedContact.contact_phone} />
              <LabeledValue label="Email"          value={savedContact.contact_email} />
              <LabeledValue label="Suburb"         value={savedContact.suburb} />
              <LabeledValue label="State"          value={savedContact.state} />
              <LabeledValue label="Website"        value={savedContact.website} />
              <LabeledValue label="Address"        value={savedContact.location} />
            </Box>
          </Paper>
        </>
      )}

      {/* ================================================================
          Follow-up date dialog (both modes)
      ================================================================ */}
      <Dialog open={followUpOpen} onClose={() => setFollowUpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Follow Up Later</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When should this contact be followed up?
          </Typography>
          <TextField
            label="Follow Up Date *"
            type="date"
            fullWidth
            autoFocus
            InputLabelProps={{ shrink: true }}
            value={followUpDate}
            onChange={(e) => { setFollowUpErr(false); setFollowUpDate(e.target.value); }}
            error={followUpErr}
            helperText={followUpErr ? 'Please select a date.' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowUpOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmFollowUp} disabled={!!saving}>
            {saving === 'follow_up' ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================
          Not-interested dialog (both modes)
          — reason is saved as a note, then status updated
      ================================================================ */}
      <Dialog open={notIntOpen} onClose={() => setNotIntOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Not Interested</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason — this will be saved as a note on the contact.
          </Typography>
          <TextField
            label="Reason *"
            fullWidth
            multiline
            rows={3}
            autoFocus
            placeholder="e.g. Already advertising elsewhere, budget constraints…"
            value={notIntReason}
            onChange={(e) => { setNotIntErr(false); setNotIntReason(e.target.value); }}
            error={notIntErr}
            helperText={notIntErr ? 'Please enter a reason.' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotIntOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmNotInt}
            disabled={!!saving}
          >
            {saving === 'not_interested' ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
